import { MessageSquare, ShieldCheck, Award, MapPin, Clock } from "lucide-react";
import { PHONE, PHONE_TEL, PHONE_HELP, PHONE_HELP_TEL } from "@/lib/api";
import LeadForm from "./LeadForm";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 lg:pt-36 pb-20 lg:pb-28 overflow-hidden grain-overlay"
    >
      {/* Background image + overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.pexels.com/photos/280229/pexels-photo-280229.jpeg"
          alt=""
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0B1B3D]/88" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1B3D]/70 via-[#0B1B3D]/80 to-[#0B1B3D]/95" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        {/* Left: Copy */}
        <div className="lg:col-span-7 text-white animate-fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#C5A059]/40 bg-[#0B1B3D]/40 backdrop-blur-sm mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-[#C5A059] animate-pulse" />
            <span className="text-[11px] uppercase tracking-[0.2em] text-[#C5A059]">
              Helping homeowners nationwide
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight mb-6">
            Facing foreclosure?
            <br />
            <span className="text-[#C5A059]">There is a way out.</span>
          </h1>

          <p className="text-base lg:text-lg text-white/80 max-w-xl leading-relaxed mb-8">
            We help homeowners stop pre-foreclosure, sell on their own terms, and
            recover surplus funds after a foreclosure sale — with empathy, privacy,
            and zero upfront cost.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <a
              href={`sms:${PHONE_TEL}`}
              data-testid="hero-phone-cta"
              className="inline-flex flex-col items-center justify-center gap-0.5 px-7 py-3 rounded-lg bg-[#C5A059] hover:bg-[#B38F4E] text-[#0B1B3D] font-semibold transition-all shadow-lg hover:shadow-xl"
            >
              <span className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Text Now: {PHONE}
              </span>
              <span className="text-[10px] uppercase tracking-[0.16em] text-[#0B1B3D]/70 font-medium">8am – 5pm</span>
            </a>
            <a
              href={`tel:${PHONE_HELP_TEL}`}
              data-testid="hero-24h-cta"
              className="inline-flex flex-col items-center justify-center gap-0.5 px-7 py-3 rounded-lg border border-white/30 text-white hover:bg-white/10 backdrop-blur-sm transition-all"
            >
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                24/7 Help: {PHONE_HELP}
              </span>
              <span className="text-[10px] uppercase tracking-[0.16em] text-white/60 font-medium">Call anytime</span>
            </a>
          </div>
          <a
            href="#services"
            data-testid="hero-secondary-cta"
            className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-[#C5A059] mb-10 transition-colors"
          >
            Or see how we help ↓
          </a>

          {/* Trust strip */}
          <div className="grid grid-cols-2 gap-6 pt-8 border-t border-white/15 max-w-md">
            <TrustStat icon={<MapPin className="h-4 w-4" />} k="All 50" v="States served" />
            <TrustStat icon={<ShieldCheck className="h-4 w-4" />} k="A+" v="Confidentiality" />
          </div>
        </div>

        {/* Right: Form */}
        <div className="lg:col-span-5 animate-fade-up delay-200" id="services">
          <LeadForm testid="hero-lead-form" compact={false} />
        </div>
      </div>
    </section>
  );
}

function TrustStat({ icon, k, v }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[#C5A059] mb-1">{icon}</div>
      <div className="font-display text-2xl lg:text-3xl text-white leading-none">{k}</div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-white/60 mt-1">{v}</div>
    </div>
  );
}
